import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SessionService from '../services/SessionService';
import {
  getSubscription,
  getPlans,
  getPayments,
  createCheckout,
  type SubscriptionData,
  type PlanData,
  type PaymentData
} from '../services/SubscriptionService';
import { LoadingSpinner } from '../components/LoadingSpinner';
import logo from '../assets/logo-horizontal.png';
import './SubscriptionPage.css';
import { homePath } from '../utils/homePath';

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  pending_payment: 'Pending Payment',
  trialing: 'Trial',
  canceled: 'Canceled'
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  succeeded: 'Paid',
  paid: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  open: 'Open',
  draft: 'Draft'
};

const RECURRENCE_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  annual: 'Annual',
  yearly: 'Annual'
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  const formatCurrency = (cents: number, currency?: string) => {
    const value = cents / 100;
    const cur = (currency || 'brl').toUpperCase();
    const symbol = cur === 'BRL' ? 'R$' : '$';
    return `${symbol} ${value.toFixed(2).replace('.', ',')}`;
  };

  const getPaymentStatusLabel = (status: string) =>
    PAYMENT_STATUS_LABELS[status?.toLowerCase()] || status;

  const isPaymentPending = (p: PaymentData) => {
    const s = (p.status || '').toLowerCase();
    return s === 'pending' || s === 'open' || s === 'draft';
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subRes, plansRes, paymentsRes] = await Promise.all([
        getSubscription(),
        getPlans(),
        getPayments()
      ]);
      setSubscription(subRes.subscription ?? null);
      setPlans(plansRes.plans ?? []);
      setPayments(paymentsRes.payments ?? []);

      if (subRes.subscription?.status === 'active') {
        localStorage.removeItem('@app/subscription_pending');
        navigate(homePath());
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = SessionService.getToken();
    if (!token) {
      navigate('/');
      return;
    }
    fetchData();
  }, [fetchData, navigate]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setToast({ type: 'success', message: 'Payment successful! Updating data...' });
      setSearchParams({});
      fetchData();
    } else if (checkout === 'cancel') {
      setToast({ type: 'info', message: 'Payment canceled. You can try again whenever you like.' });
      setSearchParams({});
    }
  }, [searchParams, fetchData, setSearchParams]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleLogout = () => {
    SessionService.clearSession();
    localStorage.removeItem('@app/subscription_pending');
    navigate('/');
  };

  const handleCheckout = async (planId: number) => {
    setCheckoutLoading(planId);
    setError(null);
    try {
      const { url } = await createCheckout(planId);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start payment');
      setCheckoutLoading(null);
    }
  };

  const pendingPayments = payments.filter(isPaymentPending);
  const paidPayments = payments.filter((p) => !isPaymentPending(p));

  return (
    <div className="sub-page">
      <header className="sub-header">
        <div className="sub-header-inner">
          <img src={logo} alt="Mends" className="sub-logo" />
          <button className="sub-logout" onClick={handleLogout}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Log out
          </button>
        </div>
      </header>

      {toast && (
        <div className={`sub-toast sub-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      <main className="sub-main">
        <div className="sub-container">
          <div className="sub-hero">
            <div className="sub-hero-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="sub-hero-text">
              <h1>Subscription Pending</h1>
              <p>To continue using the platform, complete your subscription payment.</p>
            </div>
          </div>

          {loading ? (
            <div className="sub-loading">
              <LoadingSpinner size="lg" color="primary" />
              <span>Loading...</span>
            </div>
          ) : error ? (
            <div className="sub-error-card">
              <p>{error}</p>
              <button className="sub-btn sub-btn--outline" onClick={fetchData}>
                Try again
              </button>
            </div>
          ) : (
            <>
              {subscription && (
                <section className="sub-card sub-card--highlight">
                  <div className="sub-card-header">
                    <h2>Your Subscription</h2>
                    <span className={`sub-badge sub-badge--${subscription.status}`}>
                      {STATUS_LABELS[subscription.status] || subscription.status}
                    </span>
                  </div>
                  <div className="sub-card-body">
                    <div className="sub-info-grid">
                      <div className="sub-info">
                        <span className="sub-info-label">Plan</span>
                        <span className="sub-info-value">{subscription.planName || '—'}</span>
                      </div>
                      {subscription.recurrence && (
                        <div className="sub-info">
                          <span className="sub-info-label">Recurrence</span>
                          <span className="sub-info-value">
                            {RECURRENCE_LABELS[subscription.recurrence] || subscription.recurrence}
                          </span>
                        </div>
                      )}
                      {subscription.amountCents != null && (
                        <div className="sub-info">
                          <span className="sub-info-label">Amount</span>
                          <span className="sub-info-value sub-info-value--price">
                            {formatCurrency(subscription.amountCents, subscription.currency)}
                          </span>
                        </div>
                      )}
                    </div>
                    {subscription.status === 'pending_payment' && subscription.planId && (
                      <button
                        className="sub-btn sub-btn--primary sub-btn--cta"
                        onClick={() => handleCheckout(subscription.planId)}
                        disabled={checkoutLoading === subscription.planId}
                      >
                        {checkoutLoading === subscription.planId ? (
                          <LoadingSpinner size="sm" color="white" />
                        ) : (
                          <>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                              <line x1="1" y1="10" x2="23" y2="10" />
                            </svg>
                            Pay Now
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </section>
              )}

              {(!subscription || subscription.status !== 'pending_payment') && plans.length > 0 && (
                <section className="sub-card">
                  <h2>Available Plans</h2>
                  <div className="sub-plans">
                    {plans.map((plan) => (
                      <div key={plan.id} className="sub-plan">
                        <div className="sub-plan-name">{plan.name}</div>
                        <div className="sub-plan-meta">
                          {RECURRENCE_LABELS[plan.recurrence] || plan.recurrence} · {formatCurrency(plan.amountCents, plan.currency)}
                        </div>
                        <button
                          className="sub-btn sub-btn--primary sub-btn--sm"
                          onClick={() => handleCheckout(plan.id)}
                          disabled={checkoutLoading === plan.id}
                        >
                          {checkoutLoading === plan.id ? <LoadingSpinner size="sm" color="white" /> : 'Subscribe'}
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className="sub-card">
                <div className="sub-card-header">
                  <h2>Payments & Pending</h2>
                  {pendingPayments.length > 0 && (
                    <span className="sub-badge sub-badge--pending">{pendingPayments.length} pending</span>
                  )}
                </div>
                {payments.length === 0 ? (
                  <p className="sub-empty">No payments recorded.</p>
                ) : (
                  <div className="sub-payments-list">
                    {pendingPayments.length > 0 && (
                      <div className="sub-payments-group">
                        <h3 className="sub-payments-group-title">Pending</h3>
                        {pendingPayments.map((p) => (
                          <div key={p.id} className="sub-payment-row sub-payment-row--pending">
                            <div className="sub-payment-info">
                              <span className="sub-payment-date">
                                {new Date(p.createdAt).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                              <span className="sub-payment-amount">{formatCurrency(p.amountCents, p.currency)}</span>
                            </div>
                            <span className="sub-payment-status sub-payment-status--pending">
                              {getPaymentStatusLabel(p.status)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="sub-payments-group">
                      <h3 className="sub-payments-group-title">History</h3>
                      {paidPayments.length === 0 ? (
                        <p className="sub-empty sub-empty--sm">No completed payments.</p>
                      ) : (
                        paidPayments.map((p) => (
                          <div key={p.id} className="sub-payment-row">
                            <div className="sub-payment-info">
                              <span className="sub-payment-date">
                                {new Date(p.createdAt).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                              <span className="sub-payment-amount">{formatCurrency(p.amountCents, p.currency)}</span>
                            </div>
                            <div className="sub-payment-right">
                              <span className={`sub-payment-status sub-payment-status--${p.status?.toLowerCase()}`}>
                                {getPaymentStatusLabel(p.status)}
                              </span>
                              {p.invoicePdfUrl && (
                                <a href={p.invoicePdfUrl} target="_blank" rel="noopener noreferrer" className="sub-payment-pdf">
                                  PDF
                                </a>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </section>

              {!subscription && plans.length === 0 && (
                <section className="sub-card">
                  <p className="sub-empty">No plans available. Please contact the administrator.</p>
                </section>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
